// Übersetzt eine Debug-Aufzeichnung in lesbaren Text.
//
// Zweck: Der Dispatcher kopiert einen Block, und die Ursache soll ohne Werkzeug im ersten Absatz
// stehen. Der ausführliche Bericht darunter beantwortet die Anschlussfragen (welche Aufträge, welche
// Fahrzeiten, welche Permutationen), die Rohdaten stecken zusätzlich als JSON im selben Text.

import type {
  TourDebugOperation,
  TourDebugRejectionReason,
  TourDebugSnapshot,
} from "./tour-debug.types";

const MS_PER_MINUTE = 60 * 1000;

/** Tages-Millisekunden als "HH:MM" */
export function formatDayTime(day_ms: number | null | undefined): string {
  if (day_ms == null) return "—";
  const total_minutes = Math.round(day_ms / MS_PER_MINUTE);
  const sign = total_minutes < 0 ? "-" : "";
  const absolute = Math.abs(total_minutes);
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;
  return `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/** Millisekunden als Minutenangabe */
function formatMinutes(ms: number): string {
  return `${Math.round(ms / MS_PER_MINUTE)} min`;
}

const REJECTION_LABELS: Record<TourDebugRejectionReason, string> = {
  fixtermin_reihenfolge: "Fixtermine wären in dieser Reihenfolge nicht chronologisch",
  zu_viele_entfernte_auftraege: "es hätten mehr Aufträge entfallen müssen als bei der besten Route",
  schlechter_als_beste: "zulässig, aber schlechter als die beste gefundene Route",
};

function operationLabel(operation: TourDebugOperation): string {
  const raw = operation.raw;
  const place = [raw.store_name, raw.city].filter(value => value).join(", ");
  return `Auftrag ${raw.operation_id} (Ticket ${raw.ticket_id}${place ? `, ${place}` : ""})`;
}

/**
 * Kurzdiagnose in ein bis drei Sätzen — der wichtigste Teil des ganzen Dumps.
 *
 * Hintergrund zur Fixtermin-Aussage: Der Kern verwirft seit der Verspätungs-Bewertung keine
 * Permutation mehr wegen unerreichbarer Fixtermine, und die erste geprüfte Permutation läuft ohne
 * Entfernungs-Obergrenze. Bleibt am Ende keine einzige bewertete Route übrig, kann das deshalb
 * praktisch nur an der Reihenfolge-Prüfung der Fixtermine liegen.
 */
export function buildTourDebugDiagnosis(snapshot: TourDebugSnapshot): string {
  const { flow, result } = snapshot;

  if (result.route_found) {
    const parts = [`Route gefunden: ${result.insertions.length} Aufträge`];
    if (result.unfitting_operation_ids.length) {
      parts.push(`${result.unfitting_operation_ids.length} Auftrag/Aufträge konnten nicht eingeplant werden`);
    }
    if (result.fixed_lateness_ms > 0) {
      parts.push(`Fixtermin-Verspätung insgesamt ${formatMinutes(result.fixed_lateness_ms)}`);
    }
    if (!result.unfitting_operation_ids.length && !result.fixed_lateness_ms) {
      parts.push("keine Beschränkung verletzt");
    }
    return `${parts.join(", ")}.`;
  }

  const sentences: string[] = [];
  const dominant = [...flow.rejections].sort((a, b) => b.count - a.count)[0];

  sentences.push(
    `Keine Route gefunden: alle ${flow.permutations_total} geprüften Reihenfolgen wurden verworfen` +
      (dominant ? ` — ${dominant.count}× ${REJECTION_LABELS[dominant.reason]}` : "") +
      ".",
  );

  if (dominant?.reason === "fixtermin_reihenfolge") {
    const fixed_operations = snapshot.operations
      .filter(operation => operation.core.date_fixed && operation.core.triggers_location_change)
      .sort((a, b) => a.core.operation_day_ms - b.core.operation_day_ms);

    if (fixed_operations.length > 1) {
      const chain = fixed_operations
        .map(operation => `${formatDayTime(operation.core.operation_day_ms)} ${operation.raw.store_name || operation.raw.city || "?"}`)
        .join(" → ");
      sentences.push(`Fixtermine mit Ortswechsel in zeitlicher Reihenfolge: ${chain}.`);

      const conflict = findRevisitConflict(fixed_operations);
      if (conflict) {
        sentences.push(
          `Ursache ist mit hoher Wahrscheinlichkeit, dass "${conflict.place}" zweimal angefahren werden soll ` +
            `(${formatDayTime(conflict.first_ms)} und ${formatDayTime(conflict.last_ms)}) und dazwischen ein Fixtermin ` +
            `an einem anderen Ort liegt. Die Reihenfolge-Prüfung ordnet jedem Ort genau eine Position in der Tour zu ` +
            `und kann eine Rückkehr an denselben Ort deshalb nicht abbilden.`,
        );
      }
    }
  }

  if (flow.stores.rejected_priority_operation_ids.length) {
    sentences.push(
      `Zusätzlich hängen ${flow.stores.rejected_priority_operation_ids.length} Prioritäts-Auftrag/Aufträge am ` +
        `Permutations-Limit von ${flow.stores.permutation_limit} Orten fest (IDs ${flow.stores.rejected_priority_operation_ids.join(", ")}).`,
    );
  }

  return sentences.join(" ");
}

/**
 * Sucht das Muster "derselbe Ort kommt zweimal vor, dazwischen ein anderer" in den zeitlich
 * sortierten Fixterminen — genau daran scheitert die Reihenfolge-Prüfung ausnahmslos.
 */
function findRevisitConflict(
  fixed_operations: TourDebugOperation[],
): { place: string; first_ms: number; last_ms: number } | null {
  for (let first = 0; first < fixed_operations.length; first++) {
    for (let last = first + 2; last < fixed_operations.length; last++) {
      const same_place = fixed_operations[first].core.location_id === fixed_operations[last].core.location_id;
      if (!same_place) continue;

      const has_other_place_between = fixed_operations
        .slice(first + 1, last)
        .some(operation => operation.core.location_id !== fixed_operations[first].core.location_id);

      if (has_other_place_between) {
        const operation = fixed_operations[first];
        return {
          place: operation.raw.store_name || operation.raw.city || operation.core.location_id,
          first_ms: operation.core.operation_day_ms,
          last_ms: fixed_operations[last].core.operation_day_ms,
        };
      }
    }
  }
  return null;
}

/** Ausführlicher, lesbarer Bericht ohne Rohdaten */
export function buildTourDebugReport(snapshot: TourDebugSnapshot): string {
  const { head, frame, flow, result } = snapshot;
  const lines: string[] = [];

  lines.push("=== Tourenberechnung — Debug-Bericht ===");
  lines.push("");
  lines.push(`Diagnose: ${buildTourDebugDiagnosis(snapshot)}`);
  lines.push("");

  lines.push("-- Kopfdaten --");
  lines.push(`Aufgezeichnet:   ${head.recorded_at}`);
  lines.push(`Techniker:       ${head.technician_name} (ID ${head.technician_id})`);
  lines.push(`Tourtag:         ${head.day_date}${head.day_is_today ? " (heute)" : ""}`);
  lines.push(`Rechendauer:     ${result.duration_ms} ms`);
  lines.push("");

  lines.push("-- Rahmenbedingungen --");
  lines.push(`Startort:        ${describePlace(frame.start_location)}`);
  lines.push(`Endort:          ${frame.end_location ? describePlace(frame.end_location) : "letzter Auftrag (Automatik)"}`);
  lines.push(`Zeitbudget:      ${frame.max_tour_minutes} min`);
  lines.push(
    `Arbeitsbeginn:   ${formatDayTime(frame.configured_start_ms)} (${frame.is_arrival_time ? "Ankunftszeit beim ersten Auftrag" : "Losfahrzeit"})`,
  );
  lines.push(`Jetzt-Klemmung:  ${frame.now_day_ms == null ? "keine (Tag liegt nicht heute)" : formatDayTime(frame.now_day_ms)}`);
  lines.push(`Fahrzeitfaktor:  ${frame.driving_time_factor}`);
  lines.push(`Zeitfilter:      ${frame.operation_time_filter}`);
  lines.push(
    `Mittagspause:    ${frame.automatic_lunch_break ? "automatisch" : `manuell nach Auftrag ${frame.lunch_break_after_operation_number}`}`,
  );
  lines.push("");

  lines.push(`-- Aufträge (${snapshot.operations.length}) --`);
  for (const operation of snapshot.operations) {
    const raw = operation.raw;
    const core = operation.core;
    const marks: string[] = [];
    if (core.has_started) marks.push(`gestartet ${raw.date_travel_start}`);
    if (core.date_fixed) marks.push(`FIXTERMIN ${formatDayTime(core.operation_day_ms)}`);
    if (core.same_day_sla_ms != null) marks.push(`SLA heute ${formatDayTime(core.same_day_sla_ms)}`);
    else if (raw.datesla) marks.push(`SLA ${raw.datesla}`);
    if (!core.triggers_location_change) marks.push("ohne Ortswechsel");
    if (!core.is_location_anchor) marks.push("kein Ortsanker");
    if (core.tour_index >= 0) marks.push(`auf Position ${core.tour_index} gepinnt`);
    if (!core.removable) marks.push("nicht entfernbar");
    if (core.opening_day_ms != null) marks.push(`öffnet ${formatDayTime(core.opening_day_ms)}`);

    lines.push(
      `  ${operationLabel(operation)} — ${formatMinutes(core.work_time_ms)} Arbeit` +
        (marks.length ? `, ${marks.join(", ")}` : ""),
    );
    lines.push(
      `      ${[raw.address, raw.zip_code, raw.city].filter(value => value).join(" ") || "keine Adresse"}` +
        ` · ${raw.latitude},${raw.longitude} · Prio ${raw.priority_name || "?"} · Projekt ${raw.project_id ?? "?"}`,
    );
  }
  lines.push("");

  lines.push("-- Ablauf --");
  const categories = flow.categories;
  lines.push(`Aufträge gesamt:              ${categories.total}`);
  lines.push(`  bereits gestartet:          ${categories.already_started}`);
  lines.push(`  heute verstrichene Termine: ${categories.passed_fixed}`);
  lines.push(`  auf Position gepinnt:       ${categories.pinned_tour_index}`);
  lines.push(`  Fixtermin mit Ortswechsel:  ${categories.fixed_with_location_change}`);
  lines.push(`  Tages-SLA mit Ortswechsel:  ${categories.same_day_sla_with_location_change}`);
  lines.push(`  normal mit Ortswechsel:     ${categories.normal_with_location_change}`);
  lines.push(`  Fixtermin ohne Ortswechsel: ${categories.fixed_without_location_change}`);
  lines.push(`  normal ohne Ortswechsel:    ${categories.normal_without_location_change}`);
  lines.push(`Orte permutiert (max ${flow.stores.permutation_limit}): ${flow.stores.permuted.join(" | ") || "keine"}`);
  if (flow.stores.not_permuted.length) {
    lines.push(`Orte hinten angehängt:        ${flow.stores.not_permuted.join(" | ")}`);
  }
  lines.push(`Permutationen geprüft:        ${flow.permutations_total}`);
  lines.push(`davon bewertet:               ${flow.permutations_scored}`);
  for (const rejection of [...flow.rejections].sort((a, b) => b.count - a.count)) {
    lines.push(`  verworfen ${rejection.count}×:  ${REJECTION_LABELS[rejection.reason]}`);
  }
  lines.push("");

  if (flow.candidates.length) {
    lines.push("-- Beste bewertete Routen --");
    for (const candidate of flow.candidates) {
      lines.push(
        `  [${candidate.operation_ids.join(" → ")}]` +
          ` · entfernt: ${candidate.removed_operation_ids.length ? candidate.removed_operation_ids.join(", ") : "keine"}` +
          ` · Verspätung ${formatMinutes(candidate.fixed_lateness_ms)}` +
          (candidate.duration != null ? ` · ${Math.round(candidate.duration)} min Fahrt` : "") +
          (candidate.distance != null ? ` · ${Math.round(candidate.distance)} km` : ""),
      );
    }
    lines.push("");
  }

  lines.push("-- Ergebnis --");
  lines.push(`Route gefunden:  ${result.route_found ? "ja" : "nein"}`);
  if (result.errors.length) {
    lines.push("Meldungen:");
    for (const error of result.errors) lines.push(`  - ${error}`);
  }
  if (result.insertions.length) {
    lines.push("Zeitachse:");
    for (const insertion of result.insertions) {
      lines.push(
        `  ${formatDayTime(insertion.start_time_ms)} – ${formatDayTime(insertion.end_time_ms)}  ` +
          `Auftrag ${insertion.operation_id} (Ticket ${insertion.ticket_id})`,
      );
    }
  }
  if (result.unfitting_operation_ids.length) {
    lines.push(`Nicht einplanbar: ${result.unfitting_operation_ids.join(", ")}`);
  }
  lines.push("");

  lines.push(`-- Fahrzeiten (${snapshot.distances.length} Beziehungen, roh, ohne Techniker-Faktor) --`);
  for (const distance of snapshot.distances) {
    lines.push(
      `  ${distance.from} → ${distance.to}: ${Math.round(distance.duration)} min, ${Math.round(distance.distance)} km`,
    );
  }
  lines.push("");

  lines.push(`-- Kern-Aufrufe (${snapshot.core_calls.length} mitgeschnitten) --`);
  for (const call of snapshot.core_calls) {
    lines.push(
      `  #${call.index}: [${call.operation_ids.join(" → ")}]` +
        (call.fixed_no_location_operation_ids.length
          ? ` + Termine ohne Ortswechsel [${call.fixed_no_location_operation_ids.join(", ")}]`
          : "") +
        ` · Budget ${formatMinutes(call.max_tour_time_ms)} · Entfernungs-Obergrenze ${call.highest_remove_count}`,
    );
    lines.push(
      call.result
        ? `      Ergebnis: [${call.result.partial_route_operation_ids.join(" → ")}]` +
            ` · entfernt: ${call.result.removed_operation_ids.join(", ") || "keine"}` +
            ` · Verspätung ${formatMinutes(call.result.fixed_lateness_ms)}`
        : "      Ergebnis: verworfen",
    );
  }

  return lines.join("\n");
}

function describePlace(place: { location_id: string; street: string | null; zip_code: string | null; city: string | null }): string {
  const address = [place.street, place.zip_code, place.city].filter(value => value).join(" ");
  return address ? `${address} (${place.location_id})` : place.location_id;
}

/** Vollständiger Text zum Kopieren: Bericht plus Rohdaten */
export function buildTourDebugClipboardText(snapshot: TourDebugSnapshot): string {
  return [
    buildTourDebugReport(snapshot),
    "",
    "=== Rohdaten (JSON, für den Regressionstest) ===",
    JSON.stringify(snapshot, null, 2),
  ].join("\n");
}

/** Dateiname für den Download einer Aufzeichnung */
export function buildTourDebugFileName(snapshot: TourDebugSnapshot): string {
  const date = snapshot.head.day_date.replace(/[^0-9A-Za-z]/g, "-");
  return `tour-debug_${snapshot.head.technician_id}_${date}.json`;
}
